import { jsonRpcRequest } from './client';
import type { Supplier } from '../types/supplier';
import type {
  ApiResponse,
  PagedResponse,
  GigaProduct,
  GigaProductFull,
  GigaProductDetail,
  GigaPrice,
  GigaInventory,
  GigaWarehouse,
  GigaOrderStatus,
  GigaTrackingInfo,
  ProductListing,
  ProductStatistics,
  CreateListingRequest,
  BatchCreateResult,
  DropshipOrderRequest,
} from '../types';

// ==================== 产品相关 API ====================

export interface GetProductListParams {
  page?: number;
  pageSize?: number;
  accountId?: number | null;
  category?: string;
  search?: string;
  onlyInStock?: boolean;
  useCache?: boolean;
  listingState?: string;  // 上架状态过滤: unlisted / listed / draft / error / delisted
  // 以下为直接调用GIGA API 时使用（useCache=false）
  lastUpdatedAfter?: string;
  firstArrivalDate?: string;
  sort?: number;
  queryTimeType?: 1 | 2;
  startTime?: string;
  endTime?: string;
}

/**
 * 获取产品列表（从缓存表返回聚合数据）
 * 
 * 默认使用缓存（useCache=true），返回包含完整信息的产品数据：
 * - 图片 URL
 * - 价格信息
 * - 库存数量
 * - 账号来源
 * 
 * 无需再调用 getProductDetail、getProductPrice、getInventory
 */
export async function getProductList(
  params: GetProductListParams = {},
  supplier: Supplier = 'giga',
): Promise<ApiResponse<PagedResponse<GigaProductFull>>> {
  return jsonRpcRequest('/products', {
    page: params.page || 1,
    pageSize: params.pageSize || 20,
    accountId: params.accountId,
    category: params.category,
    search: params.search,
    onlyInStock: params.onlyInStock || false,
    useCache: params.useCache !== false, // 默认 true
    listingState: params.listingState,
    // 传递给GIGA API 的参数（仅当 useCache=false 时有效）
    lastUpdatedAfter: params.lastUpdatedAfter,
    firstArrivalDate: params.firstArrivalDate,
    sort: params.sort,
    queryTimeType: params.queryTimeType,
    startTime: params.startTime,
    endTime: params.endTime,
  }, supplier);
}

/**
 * 获取产品统计信息
 */
export async function getProductStatistics(
  accountId?: number | null,
  supplier: Supplier = 'giga',
): Promise<ApiResponse<ProductStatistics>> {
  return jsonRpcRequest('/products/statistics', { accountId }, supplier);
}

export async function syncProducts(
  accountId?: number | null,
  supplier: Supplier = 'giga',
): Promise<ApiResponse<{ synced: number; errors: number }>> {
  return jsonRpcRequest('/products/sync', { accountId }, supplier);
}

/**
 * 直接从GIGA获取产品列表（不使用缓存）
 * @deprecated 建议使用 getProductList，它默认从缓存返回聚合数据
 */
export async function getProductListRaw(
  params: GetProductListParams = {}
): Promise<ApiResponse<PagedResponse<GigaProduct>>> {
  return jsonRpcRequest('/products/raw', params);
}

/**
 * 获取产品详情（批量）
 */
export async function getProductDetail(
  skus?: string[],
  productNames?: string[],
  supplier: Supplier = 'giga',
): Promise<ApiResponse<GigaProductDetail[]>> {
  return jsonRpcRequest('/products/detail', { skus, productNames }, supplier);
}

export async function getProductPrice(
  skus: string[],
  supplier: Supplier = 'giga',
): Promise<ApiResponse<GigaPrice[]>> {
  return jsonRpcRequest('/products/price', { skus }, supplier);
}

// ==================== 库存相关 API ====================

/**
 * 获取库存信息
 */
export async function getInventory(
  skus: string[],
  supplier: Supplier = 'giga',
): Promise<ApiResponse<GigaInventory[]>> {
  return jsonRpcRequest('/inventory', { skus }, supplier);
}

// ==================== 订单相关 API ====================

/**
 * 创建一件代发订单
 */
export async function createDropshipOrder(
  orderData: DropshipOrderRequest
): Promise<ApiResponse<null>> {
  return jsonRpcRequest('/order/dropship', orderData);
}

/**
 * 获取订单状态
 */
export async function getOrderStatus(
  orderNos: string[]
): Promise<ApiResponse<GigaOrderStatus[]>> {
  return jsonRpcRequest('/order/status', { orderNo: orderNos });
}

/**
 * 获取运单号
 */
export async function getTrackingNumber(
  orderNos: string[]
): Promise<ApiResponse<GigaTrackingInfo[]>> {
  return jsonRpcRequest('/order/tracking', { orderNo: orderNos });
}

// ==================== 仓库相关 API ====================

/**
 * 获取仓库地址
 */
export async function getWarehouseAddress(
  warehouseCodes?: string[]
): Promise<ApiResponse<GigaWarehouse[]>> {
  return jsonRpcRequest('/warehouse', { warehouseCodes });
}

// ==================== 产品上架管理 API ====================

/**
 * 获取上架列表
 */
export async function getListings(
  params: {
    page?: number;
    pageSize?: number;
    state?: string;
  },
  supplier: Supplier = 'giga',
): Promise<ApiResponse<PagedResponse<ProductListing>>> {
  return jsonRpcRequest('/listings', params, supplier);
}

export async function createListing(
  data: CreateListingRequest,
  supplier: Supplier = 'giga',
): Promise<ApiResponse<{ id: number; giga_sku: string; state: string }>> {
  return jsonRpcRequest('/listings/create', data, supplier);
}

export async function updateListing(
  listingId: number,
  data: Partial<ProductListing> & { resync?: boolean },
  supplier: Supplier = 'giga',
): Promise<ApiResponse<{ id: number; state?: string }>> {
  return jsonRpcRequest(`/listings/${listingId}`, data, supplier);
}

export async function delistListing(
  listingId: number,
  supplier: Supplier = 'giga',
): Promise<ApiResponse<{ id: number; state: string; shopify_status: string }>> {
  return jsonRpcRequest(`/listings/${listingId}/delist`, {}, supplier);
}

export async function relistListing(
  listingId: number,
  supplier: Supplier = 'giga',
): Promise<ApiResponse<{ id: number; state: string; shopify_status: string }>> {
  return jsonRpcRequest(`/listings/${listingId}/relist`, {}, supplier);
}

export async function retryListing(
  listingId: number,
  supplier: Supplier = 'giga',
): Promise<ApiResponse<{ id: number; state: string }>> {
  return jsonRpcRequest(`/listings/${listingId}/retry`, {}, supplier);
}

export async function deleteListing(
  listingId: number,
  supplier: Supplier = 'giga',
): Promise<ApiResponse<{ id: number; deleted: boolean }>> {
  return jsonRpcRequest(`/listings/${listingId}/delete`, {}, supplier);
}

export async function batchCreateListings(
  items: CreateListingRequest[],
  supplier: Supplier = 'giga',
): Promise<ApiResponse<BatchCreateResult>> {
  return jsonRpcRequest('/listings/batch-create', { items }, supplier);
}

/**
 * 获取上架统计数据
 */
export interface ListingStatistics {
  total: number;
  listed: number;
  draft: number;
  delisted: number;
  error: number;
  totalRevenue: number;
}

export async function getListingStatistics(
  supplier: Supplier = 'giga',
): Promise<ApiResponse<ListingStatistics>> {
  return jsonRpcRequest('/listings/statistics', {}, supplier);
}

// ==================== 聚合查询（已废弃，保留兼容） ====================
// 注意：这些函数已不再需要，因为 getProductList 现在直接返回聚合数据
// 保留这些函数是为了兼容旧代码，新代码应直接使用 getProductList

/**
 * 获取产品完整信息（详情 + 价格 + 库存）
 * @deprecated 使用 getProductList 替代，它直接返回聚合数据
 */
export async function getProductFullInfo(
  sku: string,
  supplier: Supplier = 'giga',
): Promise<{
  detail?: GigaProductDetail;
  price?: GigaPrice;
  inventory?: GigaInventory;
}> {
  const [detailRes, priceRes, inventoryRes] = await Promise.all([
    getProductDetail([sku], undefined, supplier),
    getProductPrice([sku], supplier),
    getInventory([sku], supplier),
  ]);

  return {
    detail: detailRes.success && detailRes.data ? detailRes.data[0] : undefined,
    price: priceRes.success && priceRes.data ? priceRes.data[0] : undefined,
    inventory: inventoryRes.success && inventoryRes.data ? inventoryRes.data[0] : undefined,
  };
}

/**
 * 批量获取产品价格和库存
 * @deprecated 使用 getProductList 替代，它直接返回聚合数据
 */
export async function getProductsPriceAndInventory(
  skus: string[]
): Promise<{
  prices: Map<string, GigaPrice>;
  inventories: Map<string, GigaInventory>;
}> {
  const [priceRes, inventoryRes] = await Promise.all([
    getProductPrice(skus),
    getInventory(skus),
  ]);

  const prices = new Map<string, GigaPrice>();
  const inventories = new Map<string, GigaInventory>();

  if (priceRes.success && priceRes.data) {
    priceRes.data.forEach((p) => prices.set(p.sku, p));
  }

  if (inventoryRes.success && inventoryRes.data) {
    inventoryRes.data.forEach((i) => inventories.set(i.sku, i));
  }

  return { prices, inventories };
}

/**
 * 批量获取产品详情、价格、库存（含图片）
 * @deprecated 使用 getProductList 替代，它直接返回聚合数据
 */
export async function getProductsFullInfo(
  skus: string[]
): Promise<{
  details: Map<string, GigaProductDetail>;
  prices: Map<string, GigaPrice>;
  inventories: Map<string, GigaInventory>;
}> {
  const [detailRes, priceRes, inventoryRes] = await Promise.all([
    getProductDetail(skus),
    getProductPrice(skus),
    getInventory(skus),
  ]);

  const details = new Map<string, GigaProductDetail>();
  const prices = new Map<string, GigaPrice>();
  const inventories = new Map<string, GigaInventory>();

  if (detailRes.success && detailRes.data) {
    detailRes.data.forEach((d) => details.set(d.sku, d));
  }

  if (priceRes.success && priceRes.data) {
    priceRes.data.forEach((p) => prices.set(p.sku, p));
  }

  if (inventoryRes.success && inventoryRes.data) {
    inventoryRes.data.forEach((i) => inventories.set(i.sku, i));
  }

  return { details, prices, inventories };
}
